/**
 * Typed theme accessor — the ONLY sanctioned way to reference a design token in TSX.
 *
 * Every value resolves to `var(--nv-*)` so the single source of truth stays `tokens.css`
 * (mirror of Design-handoff/reference/design-tokens.css). Never write a raw hex in a component
 * (L-2 on the client; the eslint `no-restricted-syntax` rule enforces it).
 *
 *   import { token, clinicalState } from "@/design/theme";
 *   <div style={{ color: token.ink, padding: token.s4, boxShadow: token.e1 }} />
 */

/** The canonical --nv-* variable names (exhaustive, matches tokens.css). */
export const TOKENS = {
  // marque
  navy: "--nv-navy",
  azure: "--nv-azure",
  azure600: "--nv-azure-600",
  azureSoft: "--nv-azure-soft",
  // neutres
  ink: "--nv-ink",
  graphite: "--nv-graphite",
  slate: "--nv-slate",
  steel: "--nv-steel",
  steelSoft: "--nv-steel-soft",
  line: "--nv-line",
  fieldBorder: "--nv-field-border",
  lineSoft: "--nv-line-soft",
  lineFaint: "--nv-line-faint",
  surface: "--nv-surface",
  surface2: "--nv-surface-2",
  white: "--nv-white",
  // clinique
  normal: "--nv-normal",
  low: "--nv-low",
  high: "--nv-high",
  critical: "--nv-critical",
  hold: "--nv-hold",
  success: "--nv-success",
  warn: "--nv-warn",
  danger: "--nv-danger",
  info: "--nv-info",
  // tints
  tintSuccess: "--nv-tint-success",
  tintLow: "--nv-tint-low",
  tintHigh: "--nv-tint-high",
  tintCritical: "--nv-tint-critical",
  tintHold: "--nv-tint-hold",
  // typo
  fontUi: "--nv-font-ui",
  fontMono: "--nv-font-mono",
  fsDisplay: "--nv-fs-display",
  fsH1: "--nv-fs-h1",
  fsH2: "--nv-fs-h2",
  fsH3: "--nv-fs-h3",
  fsTitle: "--nv-fs-title",
  fsBody: "--nv-fs-body",
  fsBodyLg: "--nv-fs-body-lg",
  fsLabel: "--nv-fs-label",
  fsCaption: "--nv-fs-caption",
  // rayons
  rXs: "--nv-r-xs",
  rSm: "--nv-r-sm",
  rMd: "--nv-r-md",
  rLg: "--nv-r-lg",
  rPill: "--nv-r-pill",
  // espacement
  s1: "--nv-s-1",
  s2: "--nv-s-2",
  s3: "--nv-s-3",
  s4: "--nv-s-4",
  s5: "--nv-s-5",
  s6: "--nv-s-6",
  s8: "--nv-s-8",
  // élévation
  e1: "--nv-e1",
  e2: "--nv-e2",
  e3: "--nv-e3",
  // focus
  focus: "--nv-focus",
  focusBorder: "--nv-focus-border",
  // motion
  fast: "--nv-fast",
  base: "--nv-base",
  slow: "--nv-slow",
  ease: "--nv-ease",
  easeIn: "--nv-ease-in",
  // z-index
  zSticky: "--nv-z-sticky",
  zDropdown: "--nv-z-dropdown",
  zOverlay: "--nv-z-overlay",
  zToast: "--nv-z-toast",
  zPalette: "--nv-z-palette",
  // a11y
  minTouch: "--nv-min-touch",
} as const;

export type TokenName = keyof typeof TOKENS;

/** `cssVar("ink") === "var(--nv-ink)"`. */
export function cssVar(name: TokenName): string {
  return `var(${TOKENS[name]})`;
}

/** Read a token's computed value off :root at runtime (rarely needed; prefer cssVar in styles). */
export function readToken(name: TokenName, root: HTMLElement = document.documentElement): string {
  return getComputedStyle(root).getPropertyValue(TOKENS[name]).trim();
}

/** `token.ink === "var(--nv-ink)"` — the ergonomic accessor used in component styles. */
export const token = Object.freeze(
  Object.fromEntries(
    (Object.keys(TOKENS) as TokenName[]).map((k) => [k, `var(${TOKENS[k]})`]),
  ) as Record<TokenName, string>,
);

/**
 * Clinical semantics — MUST combine color + glyph + text (never color alone; daltonisme +
 * médico-légal). Feature flag/result chips read from here.
 */
export type ClinicalStatus = "normal" | "low" | "high" | "critical" | "hold";

export interface ClinicalDescriptor {
  status: ClinicalStatus;
  /** var(--nv-*) foreground for the value/glyph. */
  color: string;
  /** var(--nv-*) tint for the chip background. */
  tint: string;
  /** Mandatory shape glyph (carries meaning without color). */
  glyph: string;
  /** FR label (i18n keys live in src/app/i18n; this is the canonical fallback text). */
  labelFr: string;
}

export const CLINICAL: Readonly<Record<ClinicalStatus, ClinicalDescriptor>> = Object.freeze({
  normal: { status: "normal", color: token.normal, tint: token.tintSuccess, glyph: "●", labelFr: "Dans l'intervalle" },
  low: { status: "low", color: token.low, tint: token.tintLow, glyph: "↓", labelFr: "Bas" },
  high: { status: "high", color: token.high, tint: token.tintHigh, glyph: "↑", labelFr: "Haut" },
  critical: { status: "critical", color: token.critical, tint: token.tintCritical, glyph: "◆", labelFr: "Valeur panique" },
  hold: { status: "hold", color: token.hold, tint: token.tintHold, glyph: "❚❚", labelFr: "QC hold / quarantaine" },
});

export function clinicalState(status: ClinicalStatus): ClinicalDescriptor {
  return CLINICAL[status];
}
